import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, Phone, Check, ChevronLeft } from "lucide-react";
import ServiceCard from "@/components/ServiceCard";
import useSeo from "@/hooks/useSeo";
import { SERVICE_BY_SLUG, SERVICES, PHONE_DISPLAY, PHONE_TEL } from "@/lib/data";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICE_BY_SLUG[slug];

  useSeo({
    title: service ? `${service.name} — RSR Australia` : "Service not found — RSR Australia",
    description: service ? `${service.short} Get a free quote from RSR Australia.` : "The service you're looking for doesn't exist.",
  });

  if (!service) {
    return (
      <section className="py-32">
        <div className="mx-auto max-w-[800px] px-5 text-center">
          <p className="overline">404</p>
          <h1 className="mt-4 font-display text-5xl font-extrabold tracking-tighter">Service not found.</h1>
          <p className="mt-4 text-muted-foreground">The service you're looking for doesn't exist or has been moved.</p>
          <Link to="/services" data-testid="not-found-back" className="mt-8 inline-flex items-center gap-2 bg-accent text-white px-5 py-3 text-sm font-semibold hover:bg-accent/90">
            <ChevronLeft className="w-4 h-4" /> All services
          </Link>
        </div>
      </section>
    );
  }

  const others = SERVICES.filter(s => s.slug !== service.slug).slice(0, 6);

  return (
    <>
      {/* Hero */}
      <section data-testid="service-detail-hero" className="relative pt-28 pb-16 lg:pt-36 lg:pb-20 border-b border-border bg-background">
        <div className="relative mx-auto max-w-[1400px] w-full px-5 sm:px-8 lg:px-12 grid grid-cols-12 gap-8 lg:gap-12 items-end">
          <div className="col-span-12 lg:col-span-7">
            <Link to="/services" data-testid="service-detail-back" className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground mb-8 uppercase tracking-[0.22em]">
              <ChevronLeft className="w-3 h-3" /> Services
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <span className="rule-accent" />
              <span className="overline">Service {service.accent}</span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] font-extrabold text-foreground max-w-3xl">
              {service.name}
            </h1>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-muted-foreground">{service.short}</p>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <div className="relative">
              <div className="absolute -top-3 -left-3 right-6 bottom-6 border border-accent/40 hidden sm:block" />
              <div className="relative aspect-[4/3] overflow-hidden border border-border">
                <img src={service.image} alt={service.name} className="w-full h-full object-cover duotone" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detail body */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-7">
            <p className="overline">What's included</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl tracking-tighter leading-[1.02] font-extrabold">
              The {service.name.toLowerCase()} work we cover.
            </h2>
            <p className="mt-6 text-base text-muted-foreground leading-relaxed max-w-2xl">{service.long}</p>

            <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-px bg-border border border-border">
              {service.bullets.map((b, i) => (
                <li key={i} data-testid={`service-bullet-${i}`} className="bg-background p-5 flex items-start gap-3">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-1" strokeWidth={2.5} />
                  <span className="text-sm text-foreground/90 leading-snug">{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-12 p-7 bg-card border border-border">
              <p className="overline">How it works</p>
              <ol className="mt-5 space-y-5">
                {[
                  ["Tell us the job", "Phone, email or the contact form — quick details and a couple of photos help."],
                  ["Get a clear quote", "Most quotes come back within one business day. Larger jobs may include a free walkthrough."],
                  ["We schedule the work", "We book a time that suits you and confirm the crew the day before."],
                  ["Job done. Walk-through.", "We finish, clean up and walk you through the work. Anything not right, we sort on the spot."],
                ].map(([t, b], i) => (
                  <li key={i} className="flex items-start gap-5">
                    <div className="font-display text-2xl font-extrabold text-accent tabular-nums w-8">{String(i + 1).padStart(2, "0")}</div>
                    <div>
                      <div className="font-semibold text-foreground">{t}</div>
                      <div className="text-sm text-muted-foreground mt-1 leading-relaxed">{b}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          <aside className="col-span-12 lg:col-span-4 lg:col-start-9">
            <div className="sticky top-28 space-y-4">
              <div className="bg-card border border-border p-7">
                <p className="overline">Free quote</p>
                <h3 className="mt-3 font-display text-2xl font-extrabold tracking-tight leading-tight">Get a price for your {service.name.toLowerCase()} job.</h3>
                <p className="mt-3 text-sm text-muted-foreground">Quick turnaround. No obligation. No upsell theatre.</p>
                <Link to={`/contact?service=${encodeURIComponent(service.name)}`} data-testid="service-detail-quote" className="mt-6 inline-flex items-center justify-between w-full gap-3 bg-accent text-white px-5 py-4 text-sm font-semibold hover:bg-accent/90 transition-colors">
                  Request a quote <ArrowUpRight className="w-4 h-4" />
                </Link>
                <a href={`tel:${PHONE_TEL}`} data-testid="service-detail-call" className="mt-3 inline-flex items-center justify-between w-full gap-3 border border-border hover:border-foreground/40 px-5 py-4 text-sm font-semibold transition-colors">
                  <span className="inline-flex items-center gap-2"><Phone className="w-4 h-4" /> {PHONE_DISPLAY}</span>
                  <span className="text-xs text-muted-foreground">7 days</span>
                </a>
              </div>

              <div className="border border-border p-7">
                <p className="overline">Coverage</p>
                <div className="mt-4 space-y-2 text-sm text-muted-foreground">
                  <div>Residential & commercial</div>
                  <div>Licensed & insured trades</div>
                  <div>Australia-wide servicing</div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Other services */}
      <section className="py-20 border-t border-border bg-card">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <p className="overline">Keep exploring</p>
              <h2 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold tracking-tighter">Other services</h2>
            </div>
            <Link to="/services" data-testid="service-detail-all-link" className="inline-flex items-center gap-2 text-sm font-semibold nav-link">
              All 14 services <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-border">
            {others.slice(0, 4).map((s) => (
              <ServiceCard key={s.slug} service={s} testIdPrefix="other-service" />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
